import React from 'react';
import { Flex, FlexDirections, GreyFlex, Title } from '@ydistri/ds';
import { Statistic } from 'antd';
import { styled } from 'styled-components';
import { COLORS } from '@ydistri/theme';

export interface StatisticItem {
  title: string;
  value: React.ReactNode;
}

export type StatisticsRowVariant = 'whiteOnGrey' | 'greyOnWhite';

interface VariantConfig {
  container: React.ElementType;
  itemPadding: string;
  itemBackgroundColor: string;
}

const VARIANTS: Record<StatisticsRowVariant, VariantConfig> = {
  whiteOnGrey: {
    container: GreyFlex,
    itemPadding: '0.7rem 1.1rem',
    itemBackgroundColor: COLORS.WHITE,
  },
  greyOnWhite: {
    container: Flex,
    itemPadding: '0.25rem 1.1rem',
    itemBackgroundColor: COLORS.GREY_LIGHT,
  },
};

const StatisticWrapper = styled.div<{ $padding: string; $backgroundColor: string }>`
  padding: ${({ $padding }) => $padding};
  border-radius: 0.4rem;
  min-width: 8rem;
  background-color: ${({ $backgroundColor }) => $backgroundColor};
`;

interface StatisticsRowProps {
  title?: string;
  stats: StatisticItem[];
  variant: StatisticsRowVariant;
  skipEmptyValues?: boolean;
}

/**
 * Renders a horizontal row of statistic cards preceded by a title, used to summarize
 * a set of values (e.g. redistribution totals). The visual style is driven by an
 * appearance-based `variant` rather than by where it is used.
 *
 * @param title - Heading shown before the statistics. Defaults to `'Summary'`.
 * @param stats - Statistic items to display, each rendered as a titled card.
 * @param variant - Appearance preset: `'whiteOnGrey'` (grey container, white cards)
 *   or `'greyOnWhite'` (plain container, grey cards).
 * @param skipEmptyValues - When `true`, items with a falsy `value` are not rendered.
 *   Defaults to `false`.
 */
const StatisticsRow: React.FC<StatisticsRowProps> = ({
  title = 'Summary',
  stats,
  variant,
  skipEmptyValues = false,
}) => {
  // eslint-disable-next-line @typescript-eslint/naming-convention -- We want to use the container as a component
  const { container: Container, itemPadding, itemBackgroundColor } = VARIANTS[variant];

  return (
    <Container
      $flexDirection={FlexDirections.ROW}
      $gap="2rem"
      $alignItems="center"
      $justifyContent="end"
      $flexGrow={1}
    >
      <Title>{title}</Title>
      {stats.map(statsItem => {
        if (skipEmptyValues && !statsItem.value) return null;

        return (
          <StatisticWrapper
            key={statsItem.title}
            $padding={itemPadding}
            $backgroundColor={itemBackgroundColor}
          >
            <Statistic title={statsItem.title} valueRender={() => statsItem.value} />
          </StatisticWrapper>
        );
      })}
    </Container>
  );
};

export default StatisticsRow;
